import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import {
    useCreateVehicleRegistrationFormMutation,
    useGetAllVehicleRegistrationFormQuery,
    useUpdateVehicleRegistrationFormMutation,
    useUploadVehicleRegistrationDocumentMutation,
    useUpdateVehicleRegistrationDocumentMutation,
    useDeleteVehicleRegistrationDocumentMutation
} from '../../../../../api';
import { useGetDealerDropdownListQuery } from '../../../../../api/endpoints/dealer';
import toastService from '../../../../../utils/toastService';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { CVRFStatus } from '../constants/cvrfStatus.js';

const useVehicleRegistrationFormData = () => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: null,
        sortOrder: null
    });
    const { th, getCurrentLanguageId } = useLanguage();
    const [submitted, setSubmitted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [apiFilters, setApiFilters] = useState({});
    const [statusFilter, setStatusFilter] = useState(CVRFStatus.Draft);
    const [apiVehicleRegistrationForm, setApiVehicleRegistrationForm] = useState();

    // Dealer dropdown için API çağrısı
    const { data: dealerList = [] } = useGetDealerDropdownListQuery({
        RowCount: 100,
        SearchText: ''
    });

    // Transform dealer list for dropdown options
    const dealerOptions = useMemo(() => {
        return dealerList.map((dealer) => ({
            label: dealer.name,
            value: dealer.dealerId
        }));
    }, [dealerList]);

    const statusOptions = useMemo(
        () => [
            { label: th('warranty.dealerVehicleRegistrationForm.status.draft'), value: CVRFStatus.Draft },
            { label: th('warranty.dealerVehicleRegistrationForm.status.sentForApproval'), value: CVRFStatus.SentForApproval },
            { label: th('warranty.dealerVehicleRegistrationForm.status.return'), value: CVRFStatus.Return },
            { label: th('warranty.dealerVehicleRegistrationForm.status.approved'), value: CVRFStatus.Approved }
        ],
        [th]
    );

    const filterFields = useMemo(
        () => [
            { field: 'startDate', headerName: th('common.startDate'), type: 'date' },
            { field: 'endDate', headerName: th('common.endDate'), type: 'date' },
            { field: 'dealerId', headerName: th('common.dealer'), type: 'dropdown', options: dealerOptions },
            { field: 'chassisNo', headerName: th('warranty.dealerVehicleRegistrationForm.filters.chassisNo.label'), placeholder: th('warranty.dealerVehicleRegistrationForm.filters.chassisNo.placeholder'), type: 'text' },
            { field: 'plateNumber', headerName: th('warranty.dealerVehicleRegistrationForm.filters.plateNumber.label'), placeholder: th('warranty.dealerVehicleRegistrationForm.filters.plateNumber.placeholder'), type: 'text' },
            { field: 'company', headerName: th('warranty.dealerVehicleRegistrationForm.filters.company.label'), placeholder: th('warranty.dealerVehicleRegistrationForm.filters.company.placeholder'), type: 'text' },
            { field: 'country', headerName: th('warranty.dealerVehicleRegistrationForm.filters.country.label'), placeholder: th('warranty.dealerVehicleRegistrationForm.filters.country.placeholder'), type: 'text' },
            { field: 'city', headerName: th('warranty.dealerVehicleRegistrationForm.filters.city.label'), placeholder: th('warranty.dealerVehicleRegistrationForm.filters.city.placeholder'), type: 'text' },
            { field: 'email', headerName: th('warranty.dealerVehicleRegistrationForm.filters.email.label'), placeholder: th('warranty.dealerVehicleRegistrationForm.filters.email.placeholder'), type: 'text' }
        ],
        [th, dealerOptions]
    );

    const filterTypeMap = useMemo(
        () => ({
            startDate: { type: 'date', matchMode: FilterMatchMode.DATE_AFTER },
            endDate: { type: 'date', matchMode: FilterMatchMode.DATE_BEFORE },
            dealerId: { type: 'number', matchMode: FilterMatchMode.EQUALS },
            chassisNo: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            plateNumber: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            company: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            country: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            city: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            email: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH }
        }),
        []
    );

    const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
    const initialFilters = useMemo(() => createInitialFilters(filterFieldNames, filterTypeMap), [filterFieldNames, filterTypeMap]);
    const [filters, setFilters] = useState(initialFilters);

    const queryParams = useMemo(() => {
        const activeFilters = { ...apiFilters };

        return {
            PageNumber: lazyParams.page + 1,
            PageSize: lazyParams.rows,
            Status: statusFilter,
            LanguageId: getCurrentLanguageId(),
            ...activeFilters,
            ...(searchTerm ? { searchTerm } : {})
        };
    }, [lazyParams, apiFilters, searchTerm, getCurrentLanguageId]);

    // DÜZELTME: burada RTK Query sonucundan var olmayan bir "totalCount" alanı
    // "isLoading" adıyla destructure ediliyordu; gerçek isLoading hiç okunmuyordu
    // (her zaman undefined/falsy kalıyordu), bu da tablo loading spinner'ının ilk
    // yüklemede hiç görünmemesine yol açıyordu. toplam kayıt sayısı zaten aşağıda
    // dataRegistrationForm?.totalCount üzerinden ayrıca okunuyor.
    const { data: dataRegistrationForm, isLoading, isFetching, error, refetch } = useGetAllVehicleRegistrationFormQuery(queryParams);
    useEffect(() => {
        setApiVehicleRegistrationForm(dataRegistrationForm?.items);
    }, [dataRegistrationForm]);

    const tabCounts = useMemo(() => {
    const map = {};
    (dataRegistrationForm?.statusCounts ?? []).forEach((i) => {
        map[i.status] = i.count;
    });
    return map;
    }, [dataRegistrationForm]);

    const totalRecords = dataRegistrationForm?.totalCount || 0;

    const [createVehicleRegistrationForm, { isLoading: isCreating }] = useCreateVehicleRegistrationFormMutation();
    const [updateVehicleRegistrationForm, { isLoading: isUpdating }] = useUpdateVehicleRegistrationFormMutation();
    const [uploadDocumentMutation, { isLoading: isUploading }] = useUploadVehicleRegistrationDocumentMutation();
    const [updateDocumentMutation, { isLoading: isUpdatingDocument }] = useUpdateVehicleRegistrationDocumentMutation();

    const [deleteDocumentMutation, { isLoading: isDeleting }] = useDeleteVehicleRegistrationDocumentMutation();

    const saveVehicleRegistrationForm = async (data, isEdit) => {
        try {
            const payload = {
                ...data,
                customerCountry: data.customerCountry?.displayValue || data.customerCountry
            };

            if (isEdit) {
                await updateVehicleRegistrationForm(payload).unwrap();
            } else {
                await createVehicleRegistrationForm(payload).unwrap();
            }
            refetch();
            return true;
        } catch (error) {
            return false;
        }
    };

    const handleSearch = useCallback((value) => {
        setSearchTerm(value);
        setLazyParams((prev) => ({
            ...prev,
            first: 0,
            page: 0
        }));
    }, []);

    const uploadVehicleRegistrationDocument = async ({ vehicleRegistrationFormId, file, description }) => {
        try {
            await uploadDocumentMutation({
                vehicleRegistrationFormId,
                file,
                description
            }).unwrap();

            toastService.success(th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.fileUploaded'));
            return true;
        } catch (error) {
            console.error('❌ Dosya yükleme hatası:', error);
            toastService.error(th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.uploadError'));
            return false;
        }
    };
    const updateVehicleRegistrationDocument = async ({ vehicleRegistrationFormId, documentId, file, description }) => {
        try {
            await updateDocumentMutation({
                vehicleRegistrationFormId,
                documentId,
                file,
                description
            }).unwrap();
            toastService.success(th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.fileUpdated'));
            return true;
        } catch (error) {
            console.error('❌ Dosya güncelleme hatası:', error);
            toastService.error(th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.updateError'));
            return false;
        }
    };

    const deleteVehicleRegistrationDocument = async (id, documentId) => {
        try {
            await deleteDocumentMutation({ id, documentId }).unwrap();
            toastService.success(th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.fileDeleted'));
            return true;
        } catch (error) {
            console.error('❌ Dosya silme hatası:', error);
            toastService.error(th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.deleteError'));
            return false;
        }
    };

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const convertedFilters = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(convertedFilters);

            const newApiFilters = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    if (key === 'startDate' || key === 'endDate') {
                        // Date alanları için özel işlem
                        const selectedDate = new Date(value);
                        const timezoneOffset = selectedDate.getTimezoneOffset();
                        const adjustedDate = new Date(selectedDate.getTime() - (timezoneOffset * 60000));
                        newApiFilters[key] = adjustedDate.toISOString();
                    } else {
                        const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                        if (key.includes('Date') && value instanceof Date) {
                            newApiFilters[pascalKey] = value.toISOString();
                        } else {
                            newApiFilters[pascalKey] = value;
                        }
                    }
                }
            });

            setApiFilters(newApiFilters);
            setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = useCallback(() => {
        const reset = createInitialFilters(filterFieldNames, filterTypeMap);
        setFilters(reset);
        setApiFilters({});
        setSearchTerm('');
        setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
    }, [filterFieldNames, filterTypeMap]);

    const onPage = useCallback((event) => {
        setLazyParams((prev) => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: event.page
        }));
    }, []);

    const onSort = useCallback((event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    }, []);

    return {
        submitted,
        filters,
        filterFields,
        apiVehicleRegistrationForm,
        isLoading,
        isFetching,
        error,
        isCreating,
        isUpdating,
        isUploading,
        isUpdatingDocument,
        isDeleting,
        CVRFStatus,
        tabCounts,
        totalRecords,
        statusFilter,
        lazyParams,
        refetch,
        setSubmitted,
        saveVehicleRegistrationForm,
        handleSearch,
        onPage,
        onSort,
        handleApplyFilters,
        handleResetFilters,
        uploadVehicleRegistrationDocument,
        updateVehicleRegistrationDocument,
        deleteVehicleRegistrationDocument,
        setStatusFilter,
        dealerOptions,
        queryParams 
    };
};

export default useVehicleRegistrationFormData;
